// ============================================================================
// ESTILOS CSS
// ============================================================================
import '../node_modules/@vertem-core/vertice-ds/src/Assets/styles/style.css';
import '../node_modules/@vertem-core/vertice-ds/src/Assets/styles/Templates/Default/style.css';
import 'components/ds/assets/reset.css';

// ============================================================================
// BIBLIOTECAS EXTERNAS
// ============================================================================
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { VertTokensProvider } from '@vertem-core/vertice-ds';
import { useLocation } from 'react-router-dom';

// ============================================================================
// COMPONENTES LOCAIS
// ============================================================================
import ClarityAnalytics from './components/ClarityAnalytics';
import Maintenance from './components/pages/Maintenance';
import { GlobalStyles } from './styles/GlobalStyles';
import { RouteEnum } from './modules/account/constantes/RouteEnum';

// ============================================================================
// UTILITÁRIOS E HELPERS
// ============================================================================
import CompatibleRouter from './utils/CompatibleRouter';
import AppRoutes from './config/Routes';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import { isMaintenanceMode } from 'utils/maintenanceMode';
import { useTheme } from 'services/ThemeService';
import { useSegmentBodyAttributes } from 'hooks/useSegmentBodyAttributes';

// ============================================================================
// CONTEXTOS E PROVIDERS
// ============================================================================
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { OtpProvider } from './modules/account/context/OtpContextProvider';
import { ModulesProvider, useModulesContext } from 'services/providers/ModulesProvider';
import { ExistingIdentifiersProvider } from './modules/account/context/ExistingIdentifiersContext';

// ============================================================================
// COMPONENTES
// ============================================================================

/**
 * Wrapper para GlobalStyles que injeta o estado de autenticação "visual"
 * com base no usuário e na rota atual.
 */
const GlobalStylesWrapper: React.FC<{ theme: any }> = ({ theme }) => {
  const { user } = useAuth();
  const location = useLocation();
  const modules = useModulesContext();

  useSegmentBodyAttributes();

  const publicPagePaths = modules
    .filter((m: any) => m.public && m.componentPosition === 'page' && m.componentPage)
    .map((m: any) => m.componentPage.startsWith('/') ? m.componentPage : `/${m.componentPage}`);

  // Rotas que devem ser tratadas como "deslogado" para fins visuais
  const unauthenticatedVisualRoutes: string[] = [
    ...publicPagePaths,
    RouteEnum.WELCOME_CONTENT,
    RouteEnum.LOGIN_USERNAME,
    RouteEnum.LOGIN_PASSWORD,
    RouteEnum.LOGOUT,
    RouteEnum.CREATE_PASSWORD,
    RouteEnum.FORGOT_PASSWORD_OTP_REQUEST,
    RouteEnum.RESET_PASSWORD,
    RouteEnum.VALIDATE_OTP_RESET_PASSWORD,
    RouteEnum.CREATE_PASSWORD_ONBOARDING,
    RouteEnum.PERSONAL,
    RouteEnum.CONTACT,
    RouteEnum.ADDRESS,
    RouteEnum.OTP_REQUEST,
    RouteEnum.OTP_VALIDATION,
    RouteEnum.SIGNUP_VALIDATION,
    RouteEnum.SIGNUP_VALIDATION_CODE,
    RouteEnum.PASSWORD_VALIDATION,
    RouteEnum.PASSWORD_VALIDATION_CODE,
    RouteEnum.INVITATION,
    RouteEnum.INVITATION_CHOICE,
    RouteEnum.ADD_INFO,
    RouteEnum.FINISH,
    RouteEnum.OTP,
    RouteEnum.COMPLEMENTARY_STEP,
    RouteEnum.SIMULATOR,
    /** Lista + detalhe (`/como-juntar-pontos/:id`) — `publicPagePaths` com `:id` não casa com `startsWith` literal. */
    RouteEnum.HOW_TO_JOIN_POINTS,
  ];

  const isOnUnauthenticatedVisualRoute = unauthenticatedVisualRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  const isAuthenticatedForBackground = Boolean(user) && !isOnUnauthenticatedVisualRoute;

  return <GlobalStyles theme={theme} isAuthenticated={isAuthenticatedForBackground} />;
};

/**
 * Componente principal da aplicação
 * Configura todos os providers e rotas da aplicação
 */
const App: React.FC = () => {
  // Hook para obter o tema e estado de carregamento
  const { theme, isLoading } = useTheme();
  
  // Instância do QueryClient para gerenciamento de estado de servidor
  const queryClient = new QueryClient();

  /**
   * Obtém o título da aplicação do localStorage ou retorna valor padrão
   * @returns Título da aplicação
   */
  const getTitle = () => {
    const theme = ApiLocalStorage.get('theme') || {};
    return theme?.header?.title || 'Programa';
  };

  /**
   * Obtém o favicon da aplicação do localStorage
   * @returns URL do favicon ou string vazia
   */
  const getFavicon = () => {
    const theme = ApiLocalStorage.get('theme') || {};
    return theme?.header?.favicon || '';
  };

  /**
   * Obtém a URL dos estilos comuns com timestamp para evitar cache
   * @returns URL dos estilos comuns com query parameter de timestamp
   */
  const getCommonsStyles = () => {
    const theme = ApiLocalStorage.get('theme') || {};
    let cssUrl = theme?.commons?.cssUrl;

    if (cssUrl) {
      // Adiciona timestamp para evitar cache do navegador
      const separator = cssUrl.includes('?') ? '&' : '?';
      cssUrl = `${cssUrl}${separator}timestamp=${Date.now()}`;
    }

    return cssUrl;
  };

  // Tapume de manutenção: substitui toda a aplicação quando ativo via env
  // (ignorável pontualmente acessando com `?debug=1` — ver utils/maintenanceMode).
  if (isMaintenanceMode()) {
    return (
      <>
        <GlobalStyles theme={theme} />
        <Maintenance />
      </>
    );
  }

  // Exibe loading enquanto o tema está sendo carregado
  if (isLoading) {
    return <div></div>;
  }

  return (
    <>
      {/* Componente de analytics do Microsoft Clarity */}
      <ClarityAnalytics />
      
      {/* Providers da aplicação em ordem hierárquica */}
      <ModulesProvider>
        <ExistingIdentifiersProvider>
          <HelmetProvider>
            {/* Configuração do head do documento HTML */}
            <Helmet>
              <title>{getTitle()}</title>
              <link rel="shortcut icon" href={getFavicon()} />
              {getCommonsStyles() && (
                <link href={getCommonsStyles()} rel="stylesheet" />
              )}
            </Helmet>
            
            {/* Router compatível com diferentes versões do React Router */}
            <CompatibleRouter>
              {/* Provider de autenticação */}
              <AuthProvider>
                {/* Provider do React Query para gerenciamento de estado de servidor */}
                <QueryClientProvider client={queryClient}>
                  {/* Provider para contexto de OTP */}
                  <OtpProvider>
                    {/* Provider de tokens de design do Vertice DS */}
                    <VertTokensProvider
                      initialTokens={{
                        ...theme?.token,
                        ...theme?.designTokens,
                        ...(theme?.components && {
                          components: theme.components,
                        }),
                      }}
                    >
                      {/* Rotas da aplicação */}
                      <AppRoutes />
                      
                      {/* Estilos globais com tema aplicado */}
                      <GlobalStylesWrapper theme={theme} />
                    </VertTokensProvider>
                  </OtpProvider>
                </QueryClientProvider>
              </AuthProvider>
            </CompatibleRouter>
          </HelmetProvider>
        </ExistingIdentifiersProvider>
      </ModulesProvider>
    </>
  );
};

export default App;
